import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  Badge,
  Alert,
  AlertIcon,
  AlertDescription,
  Spinner,
  Divider,
  Collapse,
  useToast,
  useColorModeValue,
} from "@chakra-ui/react";
import { FaBell, FaPlay, FaChevronDown, FaChevronUp } from "react-icons/fa";

const API = process.env.REACT_APP_API_URL;

// Poll cadence while a run is in progress.
const POLL_MS = 3000;

const STATUS_COLOR = { running: "blue", done: "green", failed: "red" };
const STATUS_LABEL = { running: "Running", done: "Done", failed: "Failed" };

const Alerts = () => {
  const toast = useToast();
  const userid = localStorage.getItem("userid");

  const [alerts, setAlerts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Per-alert state, keyed by alert id ('pnp' | 'chk').
  const [jobs, setJobs] = useState({}); // { [id]: job }
  const [logs, setLogs] = useState({}); // { [id]: text }
  const [showLog, setShowLog] = useState({}); // { [id]: bool }
  const [submitting, setSubmitting] = useState({}); // { [id]: bool }
  const pollRefs = useRef({});

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const mutedColor = useColorModeValue("gray.500", "gray.400");
  const logBg = useColorModeValue("gray.900", "black");

  // Load the available alert runners.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const res = await axios.post(`${API}/alerts/list`, { userid });
        if (cancelled) return;
        setAlerts(res.data?.data || []);
      } catch (e) {
        if (!cancelled) {
          const msg = e?.response?.data?.message || e.message || "Failed to load alerts";
          setLoadError(msg);
          toast({ status: "error", title: "Failed to load alerts", description: msg, duration: 5000, isClosable: true });
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userid, toast]);

  // Clear all polls on unmount.
  useEffect(
    () => () => {
      Object.values(pollRefs.current).forEach((t) => clearInterval(t));
    },
    [],
  );

  const stopPoll = (id) => {
    if (pollRefs.current[id]) {
      clearInterval(pollRefs.current[id]);
      delete pollRefs.current[id];
    }
  };

  const fetchLog = async (id, jobId) => {
    try {
      const res = await axios.get(`${API}/alerts/jobs/${encodeURIComponent(jobId)}/log`, { params: { userid } });
      const text = typeof res.data === "string" ? res.data : JSON.stringify(res.data, null, 2);
      setLogs((p) => ({ ...p, [id]: text }));
    } catch (e) {
      /* log fetch is best-effort */
    }
  };

  const pollJob = (id, jobId) => {
    stopPoll(id);
    pollRefs.current[id] = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/alerts/jobs/${encodeURIComponent(jobId)}`, { params: { userid } });
        const data = res.data?.data || {};
        setJobs((p) => ({ ...p, [id]: data }));
        if (data.status === "done" || data.status === "failed") {
          stopPoll(id);
          fetchLog(id, jobId);
          toast({
            status: data.status === "done" ? "success" : "error",
            title: data.status === "done" ? "Alert run finished" : "Alert run failed",
            description:
              data.status === "done"
                ? `If matching rows were found, the report was emailed to Helena & Dawn. Check the log for details.`
                : `Exit code ${data.exitCode}. Open the log to inspect.`,
            duration: 9000,
            isClosable: true,
          });
        }
      } catch (e) {
        // Process restart drops the in-memory job — stop quietly.
        if (e?.response?.status === 404) stopPoll(id);
      }
    }, POLL_MS);
  };

  const runAlert = async (id) => {
    setSubmitting((p) => ({ ...p, [id]: true }));
    try {
      const res = await axios.post(`${API}/alerts/run`, {
        userid,
        alert: id,
      });
      const data = res.data?.data || {};
      setJobs((p) => ({ ...p, [id]: data }));
      setLogs((p) => ({ ...p, [id]: "" }));
      toast({ status: "info", title: "Alert started", description: `${data.label || id} alert is running…`, duration: 4000, isClosable: true });
      if (data.id) pollJob(id, data.id);
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.message || e.message || "Failed to start alert";
      // 409 = already running: resume polling the existing job.
      if (status === 409 && e?.response?.data?.data?.id) {
        const data = e.response.data.data;
        setJobs((p) => ({ ...p, [id]: data }));
        pollJob(id, data.id);
      }
      toast({
        status: status === 409 ? "warning" : "error",
        title: status === 409 ? "Already running" : "Failed to start",
        description: msg,
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setSubmitting((p) => ({ ...p, [id]: false }));
    }
  };

  const toggleLog = (id) => setShowLog((p) => ({ ...p, [id]: !p[id] }));

  return (
    <Box mt={16} mx="auto" maxW="900px" px={6}>
      <Flex align="center" gap={3} mb={2}>
        <FaBell />
        <Box>
          <Heading size="lg" mb={1}>Stock Alerts</Heading>
          <Text color={mutedColor}>Run the Site Days Cover stock-alert jobs on demand. Same jobs that run on a schedule.</Text>
        </Box>
      </Flex>

      <Alert status="warning" mt={4} mb={3} borderRadius="md">
        <AlertIcon />
        <AlertDescription fontSize="sm">
          Running an alert processes the latest Site Days Cover dump and, when matches are found, emails the report to{" "}
          <strong>Helena &amp; Dawn</strong> — exactly as the scheduled cron does.
        </AlertDescription>
      </Alert>

      {isLoading ? (
        <Flex justify="center" align="center" py={16}><Spinner /></Flex>
      ) : loadError ? (
        <Flex justify="center" align="center" py={16}><Text fontSize="sm" color="red.400">{loadError}</Text></Flex>
      ) : alerts.length === 0 ? (
        <Flex justify="center" align="center" py={16}><Text fontSize="sm" color={mutedColor}>No alert runners available.</Text></Flex>
      ) : (
        alerts.map((a) => {
          const job = jobs[a.id];
          const running = job?.status === "running" || submitting[a.id];
          return (
            <Box key={a.id} mt={4} bg={cardBg} border="1px" borderColor={borderColor} borderRadius="lg" boxShadow="sm" p={5}>
              <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
                <Box>
                  <Flex align="center" gap={2}>
                    <Text fontWeight="bold" fontSize="md">{a.label} Alert</Text>
                    <Badge colorScheme="gray" variant="subtle">{a.retailer}</Badge>
                    {job && (
                      <Badge colorScheme={STATUS_COLOR[job.status] || "yellow"} px={2} py={0.5} borderRadius="md">
                        {STATUS_LABEL[job.status] || job.status}
                      </Badge>
                    )}
                  </Flex>
                  <Text fontSize="xs" color={mutedColor} mt={1}>
                    {a.script}
                    {job?.finishedAt && job.status !== "running"
                      ? ` · finished ${new Date(job.finishedAt).toLocaleTimeString()}${
                          job.exitCode != null ? ` (exit ${job.exitCode})` : ""
                        }`
                      : job?.status === "running"
                      ? " · running…"
                      : ""}
                  </Text>
                </Box>
                <Flex align="center" gap={2}>
                  {job && (logs[a.id] || job.status !== "running") && (
                    <Button
                      size="sm"
                      variant="ghost"
                      rightIcon={showLog[a.id] ? <FaChevronUp /> : <FaChevronDown />}
                      onClick={() => toggleLog(a.id)}
                    >
                      Log
                    </Button>
                  )}
                  <Button
                    leftIcon={running ? <Spinner size="xs" /> : <FaPlay />}
                    colorScheme="green"
                    onClick={() => runAlert(a.id)}
                    isDisabled={running}
                  >
                    {running ? "Running…" : "Run Alert"}
                  </Button>
                </Flex>
              </Flex>

              <Collapse in={!!showLog[a.id]} animateOpacity>
                <Divider my={4} />
                <Box
                  bg={logBg}
                  color="green.200"
                  fontFamily="mono"
                  fontSize="xs"
                  p={3}
                  borderRadius="md"
                  maxH="320px"
                  overflowY="auto"
                  whiteSpace="pre-wrap"
                >
                  {logs[a.id] || "(no output captured)"}
                </Box>
              </Collapse>
            </Box>
          );
        })
      )}
    </Box>
  );
};

export default Alerts;
